/**
 * Email verification modal component
 */
import { profileApi } from '../../api';
import { store } from '../../state';

/**
 * Show a modal to enter email verification code
 */
export function showEmailVerifyModal(email: string, onSuccess: () => void): void {
  // Create modal overlay
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-content">
      <h3>Verify Email</h3>
      <p class="text-secondary" style="margin-bottom: 1rem;">Enter the 6-digit code sent to ${email}</p>
      <input
        type="text"
        id="verify-code-input"
        class="input code-input"
        maxlength="6"
        placeholder="000000"
        inputmode="numeric"
        pattern="[0-9]*"
        autocomplete="one-time-code"
      />
      <div id="verify-error" class="text-danger" style="margin-top: 0.5rem; font-size: var(--text-small);"></div>
      <div class="modal-buttons">
        <button id="modal-cancel" class="btn btn-secondary">Cancel</button>
        <button id="modal-verify" class="btn btn-primary">Verify</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const codeInput = document.getElementById('verify-code-input') as HTMLInputElement;
  const verifyBtn = document.getElementById('modal-verify') as HTMLButtonElement;
  const cancelBtn = document.getElementById('modal-cancel') as HTMLButtonElement;
  const errorDiv = document.getElementById('verify-error')!;

  // Focus input
  codeInput.focus();

  // Handle input - only allow digits
  codeInput.addEventListener('input', () => {
    codeInput.value = codeInput.value.replace(/\D/g, '');

    // Auto-submit on 6 digits
    if (codeInput.value.length === 6) {
      verifyBtn.click();
    }
  });

  // Handle verify
  const handleVerify = async () => {
    const code = codeInput.value.trim();
    if (code.length !== 6) {
      errorDiv.textContent = 'Please enter a 6-digit code';
      return;
    }

    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Verifying...';
    errorDiv.textContent = '';

    try {
      const response = await profileApi.verifyEmail(email, code);
      if (response.data.success) {
        // Update state with new email and verified status
        const state = store.getState();
        if (state.user) {
          store.setUser({
            ...state.user,
            email: email,
            email_verified: true,
          });
        }

        // Update verification status in UI
        const statusDiv = document.getElementById('email-verify-status');
        if (statusDiv) {
          statusDiv.innerHTML = '<i class="fas fa-check-circle" style="color: var(--success);"></i> Verified';
          statusDiv.classList.remove('age-display-error');
        }

        modal.remove();
        onSuccess();
      }
    } catch (err: any) {
      console.error('Failed to verify email:', err);
      errorDiv.textContent = err.response?.data?.error || 'Invalid or expired code';
      verifyBtn.disabled = false;
      verifyBtn.textContent = 'Verify';
      codeInput.focus();
      codeInput.select();
    }
  };

  verifyBtn.addEventListener('click', handleVerify);

  // Handle cancel
  cancelBtn.addEventListener('click', () => {
    modal.remove();
  });

  // Handle escape key
  modal.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      modal.remove();
    }
  });

  // Handle enter key
  codeInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  });

  // Close on overlay click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.remove();
    }
  });
}
